import MarkdownIt from 'markdown-it';
import hljs from 'highlight.js/lib/core';
import xml from 'highlight.js/lib/languages/xml';
import php from 'highlight.js/lib/languages/php';
import bash from 'highlight.js/lib/languages/bash';
import ruby from 'highlight.js/lib/languages/ruby';
import python from 'highlight.js/lib/languages/python';
import 'highlight.js/styles/base16/tomorrow-night.css';
import javascript from 'highlight.js/lib/languages/javascript';

// Register languages
hljs.registerLanguage('xml', xml);
hljs.registerLanguage('php', php);
hljs.registerLanguage('bash', bash);
hljs.registerLanguage('ruby', ruby);
hljs.registerLanguage('python', python);
hljs.registerLanguage('javascript', javascript);

export class MarkdownRenderer {
    constructor() {
        this.md = new MarkdownIt({
            html: true,
            linkify: true,
            typographer: true,
            highlight: (str, lang) => {
                if (lang && hljs.getLanguage(lang)) {
                    try {
                        const highlighted = hljs.highlight(str, { 
                            language: lang, 
                            ignoreIllegals: true 
                        }).value;
                        return `<pre class="language-${this.SanitizeHTML(lang)}" lxk-data-language="${this.SanitizeHTML(lang)}"><code>${highlighted}</code></pre>`;
                    } catch (__) {}
                }
                return `<pre><code>${md.utils.escapeHtml(str)}</code></pre>`;
            }
        });
    }

    SanitizeHTML(html) {
        return html
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }

    wrapCodeBlockWithUI(pre) {
        const wrapper = document.createElement('div');
        wrapper.className = 'lxk-code-block-wrapper lxk-drop-shadow-xl lxk-max-w-xl lxk-mx-auto';
        
        const header = document.createElement('div');
        header.className = 'lxk-code-block-header';
        
        const windowControls = document.createElement('div');
        windowControls.className = 'lxk-window-controls';
        windowControls.innerHTML = `
            <div class="lxk-dot lxk-close"></div>
            <div class="lxk-dot lxk-minimize"></div>
            <div class="lxk-dot lxk-maximize"></div>
        `;
        
        const languageLabel = document.createElement('span');
        languageLabel.className = 'lxk-language-label';
        languageLabel.textContent = pre.getAttribute('lxk-data-language') || 'text';
        
        header.append(windowControls, languageLabel);
        pre.parentNode.insertBefore(wrapper, pre);
        wrapper.append(header, pre);
    }

    wrapImageWithUI(img) {
        img.style.margin = '0';
        img.style.maxWidth = '100%';
    
        const wrapper = document.createElement('div');
        wrapper.className = 'lxk-image-wrapper lxk-drop-shadow-xl';
        
        const parent = img.parentNode;
        parent.insertBefore(wrapper, img);
        wrapper.appendChild(img);
    }
    
    wrapTableWithUI(table) {
        const wrapper = document.createElement('div');
        wrapper.className = 'lxk-table-wrapper';
        
        table.parentNode.insertBefore(wrapper, table);
        wrapper.appendChild(table);
    }

    async copyToClipboard(text) {
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(text);
                return true;
            }
            
            const textArea = document.createElement('textarea');
            textArea.value = text;
            textArea.style.cssText = 'position:fixed;left:-999999px;top:-999999px;';
            document.body.appendChild(textArea);
            
            textArea.select();
            document.execCommand('copy');
            textArea.remove();
            return true;
        } catch (error) {
            return false;
        }
    }

    createCopyButton(pre) {
        const copyButton = document.createElement('button');
        copyButton.className = 'lxk-copy-button';
        copyButton.innerHTML = this.getCopyButtonHTML();
        
        copyButton.addEventListener('click', async () => {
            const code = pre.querySelector('code').textContent;
            try {
                const success = await this.copyToClipboard(code);
                copyButton.innerHTML = success ? this.getCopiedButtonHTML() : this.getErrorButtonHTML();
                setTimeout(() => {
                    copyButton.innerHTML = this.getCopyButtonHTML();
                }, 2000);
            } catch (err) {
                copyButton.innerHTML = this.getErrorButtonHTML();
                setTimeout(() => {
                    copyButton.innerHTML = this.getCopyButtonHTML();
                }, 2000);
            }
        });

        return copyButton;
    }

    tableToCSV(table) {
        const rows = Array.from(table.querySelectorAll('tr'));
        return rows.map(row => {
            const cells = Array.from(row.querySelectorAll('th, td'));
            return cells.map(cell => {
                let text = cell.textContent.trim();
                if (text.includes(',') || text.includes('"')) {
                    text = `"${text.replace(/"/g, '""')}"`;
                }
                return text;
            }).join(',');
        }).join('\n');
    }

    getCopyButtonHTML() {
        return `
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="lxk-w-6 lxk-h-6">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184" />
            </svg>
            <span>Copy</span>
        `;
    }
    
    getCopiedButtonHTML() {
        return `
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
                <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 7.5V6.108c0-1.135.845-2.098 1.976-2.192.373-.03.748-.057 1.123-.08M15.75 18H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08M15.75 18.75v-1.875a3.375 3.375 0 0 0-3.375-3.375h-1.5a1.125 1.125 0 0 1-1.125-1.125v-1.5A3.375 3.375 0 0 0 6.375 7.5H5.25m11.9-3.664A2.251 2.251 0 0 0 15 2.25h-1.5a2.251 2.251 0 0 0-2.15 1.586m5.8 0c.065.21.1.433.1.664v.75h-6V4.5c0-.231.035-.454.1-.664M6.75 7.5H4.875c-.621 0-1.125.504-1.125 1.125v12c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V16.5a9 9 0 0 0-9-9Z" />
            </svg>
            <span>Copied!</span>
        `;
    }
    
    getErrorButtonHTML() {
        return `
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
            <span>Failed to copy</span>
        `;
    }

    getCSVButtonHTML() {
        return `
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="lxk-w-5 lxk-h-5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
            </svg>
            <span>Get CSV</span>
        `;
    }

    getImageDownloadButtonHTML() {
        return `
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="lxk-w-5 lxk-h-5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
            </svg>
        `;
    }

    async render(markdown, container) {
        // Render markdown to HTML
        const html = this.md.render(markdown);
        container.innerHTML = html;
    
        // Process code blocks
        container.querySelectorAll('pre[lxk-data-language]').forEach(pre => {
            this.wrapCodeBlockWithUI(pre);
        });
    
        // Process images
        container.querySelectorAll('img').forEach(img => {
            this.wrapImageWithUI(img);
        });
    
        // Process tables
        container.querySelectorAll('table').forEach(table => {
            this.wrapTableWithUI(table);
        });
    
        // Highlight code blocks
        container.querySelectorAll('pre code').forEach((block) => {
            hljs.highlightElement(block);
        });
    
        // Open links in new tab
        container.querySelectorAll('a').forEach(link => {
            link.setAttribute('target', '_blank');
            link.setAttribute('rel', 'noopener noreferrer');
        });
    }

    cleanup(container) {
        container.querySelectorAll('.lxk-code-block-wrapper').forEach(wrapper => {
            const pre = wrapper.querySelector('pre');
            if (pre) wrapper.parentNode.insertBefore(pre, wrapper);
            wrapper.remove();
        });
    
        container.querySelectorAll('.lxk-image-wrapper').forEach(wrapper => {
            const img = wrapper.querySelector('img');
            if (img) wrapper.parentNode.insertBefore(img, wrapper);
            wrapper.remove();
        });
    
        container.querySelectorAll('.lxk-table-wrapper').forEach(wrapper => {
            const table = wrapper.querySelector('table');
            if (table) wrapper.parentNode.insertBefore(table, wrapper);
            wrapper.remove();
        });
    }
}


export function lxkCleanMarkdown(markdownText) {
    if (!markdownText) return '';

    return markdownText
        // Remove headers (#)
        .replace(/^#+\s*/gm, '')
        
        // Remove bold/italic
        .replace(/(\*\*|__)(.*?)\1/g, '$2')
        .replace(/(\*|_)(.*?)\1/g, '$2')
        
        // Remove code blocks and inline code
        .replace(/```[\s\S]*?```/g, '')
        .replace(/`([^`]+)`/g, '$1')
        
        // Remove blockquotes
        .replace(/^\s*>\s*/gm, '')
        
        // Remove horizontal rules
        .replace(/^(\s*[-*_]){3,}\s*$/gm, '')
        
        // Remove inline links and images
        .replace(/!?\[(.*?)\]\(.*?\)/g, '$1')
        
        // Remove ordered/unordered lists
        .replace(/^\s*[\d-*+]\s+/gm, '')
        
        // Remove HTML tags
        .replace(/<[^>]*>/g, '')
        
        // Remove strikethrough
        .replace(/~~(.*?)~~/g, '$1')
        
        // Remove extra whitespace
        .replace(/\s+/g, ' ')
        .trim();
}